/* eslint-disable react/button-has-type -- it's oke */
import {
  Suspense,
  lazy,
  useMemo,
  type ButtonHTMLAttributes,
  type FC,
  type ReactNode
} from "react";
import type { Placement } from "@floating-ui/react";
import type { IconSvgElement } from "@hugeicons/react";
import { cx } from "../../utils/classnames";
import type { IconSize } from "../IconBridge";
import { IconBridge } from "../IconBridge";
import { Loading } from "./loading-svg";

// Lazy load Tooltip components
const Tooltip = lazy(() =>
  import("../Tooltip").then((module) => ({ default: module.Tooltip }))
);
const TooltipContent = lazy(() =>
  import("../Tooltip").then((module) => ({ default: module.TooltipContent }))
);
const TooltipTrigger = lazy(() =>
  import("../Tooltip").then((module) => ({ default: module.TooltipTrigger }))
);

export enum ButtonVariant {
  PRIMARY = "PRIMARY",
  BASE = "BASE",
  TEXT = "TEXT",
  DANGER = "DANGER"
}

export enum ButtonSize {
  XS = "XS",
  SM = "SM",
  BASE = "BASE",
  LG = "LG"
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- we will change Icon props a lot
type UntitledIcon = FC<any>;
interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  Icon?: UntitledIcon;
  HugeIcon?: IconSvgElement;
  iconPosition?: "left" | "right";
  iconStrokeWidth?: number;
  iconClassName?: string;
  type?: "submit" | "reset" | "button";
  loading?: boolean;
  loadingMode?: "hide-label" | "keep-label";
  borderLess?: boolean;
  disabled?: boolean;
  tooltip?: string | ReactNode;
  tooltipZIndex?: number;
  tooltipPlacement?: Placement;
  tooltipDelay?: number;
  loadingText?: string;
}

const buttonVariant = (modifiers?: { borderless?: boolean }) => ({
  [ButtonVariant.PRIMARY]:
    "bg-primary-500 text-white stroke-white border-transparent text-white hover:bg-primary-600",
  [ButtonVariant.DANGER]:
    "bg-danger-500 text-white stroke-white border-transparent text-white hover:bg-danger-600",
  [ButtonVariant.BASE]: modifiers?.borderless
    ? "bg-content-1000-inverted stroke-content-500 text-content-500 hover:bg-alpha-40 hover:text-content-600 hover:stroke-content-600 disabled:hover:bg-content-1000-inverted disabled:stroke-alpha-100 disabled:cursor-not-allowed"
    : "bg-content-1000-inverted text-content-1000 stroke-content-1000 border-alpha-100 hover:bg-alpha-40 disabled:text-alpha-100 disabled:hover:bg-content-1000-inverted disabled:stroke-alpha-100 disabled:cursor-not-allowed",
  [ButtonVariant.TEXT]:
    "text-primary-500 stroke-primary-500 hover:text-primary-600 hover:stroke-primary-600 disabled:text-alpha-100"
});

const buttonSize = (isInline: boolean) => {
  return {
    [ButtonSize.XS]: cx(
      "text-xs",
      !isInline ? "px-4 min-h-[34px] h-[34px] max-h-[34px] rounded-lg" : ""
    ),
    [ButtonSize.SM]: cx(
      "text-xs",
      !isInline ? "px-4 min-h-[40px] h-[40px] max-h-[40px] rounded-lg" : ""
    ),
    [ButtonSize.BASE]: cx(
      "text-base",
      !isInline ? "px-5 min-h-[48px] h-[48px] max-h-[48px] rounded-lg" : ""
    ),
    [ButtonSize.LG]: cx(
      "text-base",
      !isInline ? "px-8 min-h-[56px] h-[56px] max-h-[56px] rounded-lg" : ""
    )
  };
};

export const Button: FC<ButtonProps> = ({
  children,
  className,
  loading,
  loadingMode = "hide-label",
  Icon,
  HugeIcon,
  iconPosition = "left",
  iconStrokeWidth,
  iconClassName,
  variant = ButtonVariant.BASE,
  size = ButtonSize.BASE,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- need it
  loadingText,
  borderLess,
  type,
  disabled,
  tooltip,
  tooltipZIndex,
  tooltipPlacement,
  tooltipDelay,
  ...rest
}) => {
  if (
    process.env.NODE_ENV !== "production" &&
    borderLess &&
    variant !== ButtonVariant.BASE
  ) {
    throw new Error("Button with prop borderLess must have variant BASE");
  }

  const isInline = Boolean(
    variant === ButtonVariant.TEXT || className?.includes("inline")
  );

  const hideIconWhileLoading = Boolean(loading && loadingMode === "keep-label");
  const hideLabelWhileLoading =
    Boolean(loading) && loadingMode !== "keep-label";

  const baseClass = useMemo(() => {
    return cx(
      isInline
        ? "inline"
        : "flex gap-2.5 items-center border-box justify-center whitespace-nowrap text-ellipsis overflow-hidden",
      "stroke-2 font-bold group transition-all outline-inherit focus:outline-1 focus:outline-primary-500"
    );
  }, [isInline]);

  const buttonVariantClass = useMemo(() => {
    return buttonVariant({ borderless: borderLess })[variant];
  }, [variant, borderLess]);
  const buttonSizeClass = useMemo(() => {
    return buttonSize(isInline)[size];
  }, [isInline, size]);

  if (tooltip) {
    return (
      <Suspense fallback={null}>
        <Tooltip placement={tooltipPlacement} delay={tooltipDelay}>
          <TooltipTrigger className={className}>
            <button
              className={cx(
                baseClass,
                buttonVariantClass,
                buttonSizeClass,
                borderLess || variant === ButtonVariant.TEXT
                  ? "border-[none]"
                  : "border",
                variant === ButtonVariant.PRIMARY && disabled && !loading
                  ? "!bg-alpha-40 !text-content-100 font-bold"
                  : "",
                className
              )}
              disabled={disabled || loading}
              type={type ?? "button"}
              {...rest}
            >
              <span className={loading ? "" : "hidden"}>
                <Loading />
              </span>
              {iconPosition === "left" && !hideIconWhileLoading ? (
                <IconBridge
                  Icon={Icon}
                  HugeIcon={HugeIcon}
                  className={cx("shrink-0", iconClassName)}
                  size={size as unknown as IconSize}
                  strokeWidth={iconStrokeWidth}
                />
              ) : null}
              <span className={hideLabelWhileLoading ? "hidden" : ""}>
                {children}
              </span>
              {iconPosition === "right" && !hideIconWhileLoading ? (
                <IconBridge
                  Icon={Icon}
                  HugeIcon={HugeIcon}
                  className={cx("shrink-0", iconClassName)}
                  size={size as unknown as IconSize}
                  strokeWidth={iconStrokeWidth}
                />
              ) : null}
            </button>
          </TooltipTrigger>
          <TooltipContent
            className="bg-alpha-800 rounded-lg border p-4 text-sm text-white"
            style={
              tooltipZIndex
                ? {
                    zIndex: tooltipZIndex
                  }
                : undefined
            }
          >
            {tooltip}
          </TooltipContent>
        </Tooltip>
      </Suspense>
    );
  }

  return (
    <button
      className={cx(
        baseClass,
        buttonVariantClass,
        buttonSizeClass,
        borderLess || variant === ButtonVariant.TEXT
          ? "border-[none]"
          : "border",
        variant === ButtonVariant.PRIMARY && disabled && !loading
          ? "!bg-alpha-40 !text-content-100 font-bold"
          : "",
        variant === ButtonVariant.BASE && loading ? "!cursor-none" : "",
        className
      )}
      disabled={disabled || loading}
      type={type ?? "button"}
      {...rest}
    >
      <span className={loading ? "" : "hidden"}>
        <Loading />
      </span>
      {iconPosition === "left" && !hideIconWhileLoading ? (
        <IconBridge
          className={cx("shrink-0", iconClassName)}
          Icon={Icon}
          HugeIcon={HugeIcon}
          size={size as unknown as IconSize}
          strokeWidth={iconStrokeWidth}
        />
      ) : null}
      <span className={hideLabelWhileLoading ? "hidden" : ""}>{children}</span>
      {iconPosition === "right" && !hideIconWhileLoading ? (
        <IconBridge
          className={cx("shrink-0", iconClassName)}
          Icon={Icon}
          HugeIcon={HugeIcon}
          size={size as unknown as IconSize}
          strokeWidth={iconStrokeWidth}
        />
      ) : null}
    </button>
  );
};
